import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { certificationAPI, courseAPI } from '../../services/api';
import Modal from '../../components/Modal';
import { LoadingSpinner, EmptyState } from '../../components/UI';

const AdminCertifications = () => {
  const [certs, setCerts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, cert: null });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, cs] = await Promise.all([certificationAPI.list(), courseAPI.list()]);
      setCerts(c.data);
      setCourses(cs.data);
    } catch (e) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this certification template?')) return;
    try {
      await certificationAPI.delete(id);
      toast.success('Certification deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  // Courses without an attached cert yet
  const certCourseIds = certs.map((c) => c.course?._id);
  const availableCourses = courses.filter((c) => !certCourseIds.includes(c._id));

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1>Certification Templates</h1>
          <p className="subtitle">{certs.length} templates · attached to {certs.length} courses</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setModal({ open: true, cert: null })}
          disabled={availableCourses.length === 0}
          title={availableCourses.length === 0 ? 'All courses already have certifications' : ''}
        >
          + New Certification
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : certs.length === 0 ? (
        <EmptyState
          icon="🏅"
          title="No certifications yet"
          message="Create a certification template and attach it to a course"
        />
      ) : (
        <div className="grid grid-2">
          {certs.map((c) => (
            <div key={c._id} className="card">
              <div className="flex justify-between items-center">
                <span
                  className="badge"
                  style={{ background: c.accentColor + '22', color: c.accentColor }}
                >
                  {c.template}
                </span>
                {!c.isActive && <span className="badge badge-rejected">Inactive</span>}
              </div>
              <h3 className="mt-2">{c.title}</h3>
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                {c.description || 'No description'}
              </p>
              <div className="mt-2" style={{ fontSize: '0.85rem' }}>
                <div>📚 <strong>Course:</strong> {c.course?.title}</div>
                <div>🏢 <strong>Issuer:</strong> {c.issuingOrganization}</div>
                <div>🎯 <strong>Min score:</strong> {c.minScore}%</div>
                <div>⏳ <strong>Validity:</strong> {c.validityMonths ? `${c.validityMonths} months` : 'Lifetime'}</div>
                <div>✍️ <strong>Signatory:</strong> {c.signatoryName || '—'} ({c.signatoryTitle})</div>
              </div>
              <div className="flex gap-1 mt-2">
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ flex: 1 }}
                  onClick={() => setModal({ open: true, cert: c })}
                >
                  Edit
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c._id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CertificationModal
        open={modal.open}
        cert={modal.cert}
        availableCourses={availableCourses}
        allCourses={courses}
        onClose={() => setModal({ open: false, cert: null })}
        onSaved={fetchAll}
      />
    </div>
  );
};

const CertificationModal = ({ open, cert, availableCourses, allCourses, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (cert) {
      setForm({
        title: cert.title,
        description: cert.description,
        issuingOrganization: cert.issuingOrganization,
        validityMonths: cert.validityMonths || '',
        minScore: cert.minScore,
        template: cert.template,
        accentColor: cert.accentColor,
        signatoryName: cert.signatoryName,
        signatoryTitle: cert.signatoryTitle,
        course: cert.course?._id || cert.course,
        isActive: cert.isActive,
      });
    } else {
      setForm({
        title: '',
        description: '',
        issuingOrganization: 'HillBoat Study Academy',
        validityMonths: '',
        minScore: 60,
        template: 'classic',
        accentColor: '#92400e',
        signatoryName: '',
        signatoryTitle: 'Director of Education',
        course: '',
        isActive: true,
      });
    }
  }, [cert, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        minScore: Number(form.minScore),
        validityMonths: form.validityMonths ? Number(form.validityMonths) : null,
      };
      if (cert) {
        delete payload.course;
        await certificationAPI.update(cert._id, payload);
      } else {
        await certificationAPI.create(payload);
      }
      toast.success(cert ? 'Certification updated' : 'Certification created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // For edit, show all courses so the current one displays
  const courseOptions = cert ? allCourses : availableCourses;

  return (
    <Modal open={open} onClose={onClose} title={cert ? 'Edit Certification' : 'New Certification'} size="lg">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Attach to Course</label>
          <select
            className="select"
            value={form.course || ''}
            onChange={(e) => setForm({ ...form, course: e.target.value })}
            required
            disabled={!!cert}
          >
            <option value="">Select course</option>
            {courseOptions.map((c) => (
              <option key={c._id} value={c._id}>
                {c.title}
              </option>
            ))}
          </select>
          {cert && (
            <small className="text-muted">Course cannot be changed after creation</small>
          )}
        </div>

        <div className="form-group">
          <label>Certification Title</label>
          <input
            className="input"
            value={form.title || ''}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            placeholder="e.g. Full Stack Web Developer — Certified"
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            className="textarea"
            value={form.description || ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What this certification represents"
          />
        </div>

        <div className="grid grid-2">
          <div className="form-group">
            <label>Issuing Organization</label>
            <input
              className="input"
              value={form.issuingOrganization || ''}
              onChange={(e) => setForm({ ...form, issuingOrganization: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Template Style</label>
            <select
              className="select"
              value={form.template || 'classic'}
              onChange={(e) => setForm({ ...form, template: e.target.value })}
            >
              <option value="classic">Classic</option>
              <option value="modern">Modern</option>
              <option value="elegant">Elegant</option>
            </select>
          </div>
          <div className="form-group">
            <label>Minimum Score (%)</label>
            <input
              className="input"
              type="number"
              min="0"
              max="100"
              value={form.minScore || 0}
              onChange={(e) => setForm({ ...form, minScore: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Validity (months) — leave blank for lifetime</label>
            <input
              className="input"
              type="number"
              value={form.validityMonths || ''}
              onChange={(e) => setForm({ ...form, validityMonths: e.target.value })}
              placeholder="e.g. 24"
            />
          </div>
          <div className="form-group">
            <label>Signatory Name</label>
            <input
              className="input"
              value={form.signatoryName || ''}
              onChange={(e) => setForm({ ...form, signatoryName: e.target.value })}
              placeholder="e.g. Dr. Sarah Mitchell"
            />
          </div>
          <div className="form-group">
            <label>Signatory Title</label>
            <input
              className="input"
              value={form.signatoryTitle || ''}
              onChange={(e) => setForm({ ...form, signatoryTitle: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Accent Color</label>
            <input
              className="input"
              type="color"
              value={form.accentColor || '#92400e'}
              onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
              style={{ height: 48, padding: 4 }}
            />
          </div>
          {cert && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={form.isActive ?? true}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>
          )}
        </div>

        <div className="flex gap-1 justify-between mt-3">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Certification'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AdminCertifications;
